/**
 * Fresh CSRF tokens for edge-cached pages.
 *
 * Guest pages (/, /tools/*, /templates/*, the SEO catalog, cached 404s) are served from the
 * Cloudflare edge cache, so the authenticity tokens baked into their forms and <meta name="csrf-token">
 * belong to whichever session warmed the cache. A cookie-less visitor's POST with that token fails
 * CSRF. Before such a form submits, fetch GET /users/sign_in.json (Users::SessionsController#render_fresh_csrf_token):
 * it starts a session for this visitor and returns a token for it, which is swapped into every form
 * on the page and the meta tag.
 *
 * The page HTML itself stays identical for every visitor, and the cached GET never sets a cookie.
 */

const requests = new Map();
const completed = new Set();
let lastToken = null;

function requestUrl(url, returnTo) {
  if (!returnTo) return url;
  // The tool dialog's url already carries ?pending_unlock=<slug>.
  return `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams({ return_to: returnTo })}`;
}

/**
 * Fetches a token for this visitor's session and applies it page-wide. Resolves true on success.
 * One request per url/returnTo pair; a failed request can be retried.
 * returnTo (optional) is stored as Devise's after-sign-in path.
 */
export function refreshCsrfToken(url, { returnTo } = {}) {
  if (!url || typeof fetch !== 'function') return Promise.resolve(false);

  const key = requestUrl(url, returnTo);
  // Re-apply on reuse: a Turbo visit may have swapped in a page without the token.
  if (requests.has(key)) {
    return requests.get(key).then((ok) => {
      if (ok && lastToken) applyCsrfToken(lastToken);
      return ok;
    });
  }

  const request = fetch(key, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const token = data?.csrf_token;
      if (!token) throw new Error('no csrf token');

      lastToken = token;
      applyCsrfToken(token);
      completed.add(key);
      return true;
    })
    .catch(() => {
      requests.delete(key);
      return false;
    });
  requests.set(key, request);
  return request;
}

function applyCsrfToken(token) {
  document.querySelectorAll('input[name="authenticity_token"]').forEach((input) => {
    input.value = token;
  });
  let meta = document.querySelector('meta[name="csrf-token"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'csrf-token';
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', token);
}

/**
 * Submit-event guard: holds the first native form submit until a fresh token is in place
 * (and, with returnTo, stored), then resubmits with the same submitter. Later submits pass straight through, and so does a
 * submit another handler already took over (defaultPrevented). If the refresh fails the form
 * still submits once, with whatever token it has.
 */
export function submitWithFreshCsrfToken(event, url, options = {}) {
  if (event.defaultPrevented || !url || completed.has(requestUrl(url, options.returnTo))) return;

  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.dataset.freshCsrfResubmitting === 'true') return;

  event.preventDefault();
  const submitter = event.submitter;
  refreshCsrfToken(url, options).then(() => {
    form.dataset.freshCsrfResubmitting = 'true';
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
      } else {
        form.submit();
      }
    } finally {
      delete form.dataset.freshCsrfResubmitting;
    }
  });
}
